import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { MapPin, Navigation } from 'lucide-react';

/**
 * Reusable locate + map widget.
 * Keeps Checkout behavior: reverse-geocode and autofill address fields.
 */
const LocateViaLiveMap = ({
  title = 'Shipping Address',
  formData,
  onFormDataChange,
  coordinates,
  onCoordinatesChange,
  containerClassName = 'w-full h-28',
  iframeTitle = 'Google Map Interface Viewport',
  defaultQuery = 'India',
  locateButtonText = 'Locate via Live Map'
}) => {
  const [geoLoading, setGeoLoading] = useState(false);

  const handleDetectLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    setGeoLoading(true);
    const toastId = toast.loading('Pinpointing your location...');

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        const nextCoords = { lat: latitude, lng: longitude };
        onCoordinatesChange?.(nextCoords);

        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`
          );
          const data = await response.json();

          if (data && data.address) {
            const addr = data.address;
            const detectedStreet = [addr.road || '', addr.suburb || '', addr.neighbourhood || '']
              .filter(Boolean)
              .join(', ') || 'Detected Location';

            onFormDataChange?.((prev) => ({
              ...prev,
              street: detectedStreet,
              city: addr.city || addr.town || addr.village || '',
              state: addr.state || '',
              zipCode: addr.postcode || '',
              country: addr.country || ''
            }));

            toast.success('Address autofilled successfully!', { id: toastId });
          } else {
            toast.error('Coordinates captured, but could not resolve address names.', { id: toastId });
          }
        } catch (error) {
          console.error(error);
          toast.error('Failed to parse geographical address details.', { id: toastId });
        } finally {
          setGeoLoading(false);
        }
      },
      (error) => {
        console.error(error);
        toast.error(error.message || 'Location access denied. Check your browser permissions.', { id: toastId });
        setGeoLoading(false);
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  const iframeSrc = coordinates
    ? `https://maps.google.com/maps?q=${coordinates.lat},${coordinates.lng}&t=&z=16&ie=UTF8&iwloc=&output=embed`
    : `https://maps.google.com/maps?q=${encodeURIComponent(
        formData?.street || formData?.city || defaultQuery
      )}&t=&z=6&ie=UTF8&iwloc=&output=embed`;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
          <MapPin className="text-purple-600" size={22} /> {title}
        </h2>

        <button
          type="button"
          onClick={handleDetectLocation}
          disabled={geoLoading}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-700 hover:bg-purple-100 disabled:opacity-50 text-xs font-semibold rounded-xl transition-all border border-purple-200 shadow-sm"
        >
          <Navigation size={14} className={geoLoading ? 'animate-spin' : ''} />
          {geoLoading ? 'Pinpointing...' : locateButtonText}
        </button>
      </div>

      <div
        className="w-full h-28 rounded-xl overflow-hidden border border-gray-200 bg-slate-100 shadow-inner relative"
        style={containerClassName ? undefined : undefined}
      >
        <iframe
          title={iframeTitle}
          width="100%"
          height="100%"
          frameBorder="0"
          loading="lazy"
          allowFullScreen
          src={iframeSrc}
          className="absolute inset-0 w-full h-full"
        />
      </div>
    </div>
  );
};

export default LocateViaLiveMap;

